'use client';

import { c as _c } from "react/compiler-runtime";
import { type Choice, ChoiceList, Heading } from '@jaaq/hub-ui';
import { Code, Globe, Smartphone } from 'lucide-react';
import type { ReactElement } from 'react';
import { OnboardingShell } from '#components/onboarding-shell';
import type { IconKey } from '#lib/content/types';
import { useOnboardingContent, useOnboardingState } from '#lib/onboarding-provider';
import type { DigitalSetup } from '#lib/steps/state';

/**
 * Step 2/3 — digital-setup question (multi-select). Copy + choices come
 * from the content layer; icon registry stays local.
 */
export const DigitalSetupStep = () => {
  const $ = _c(20);
  const {
    onboardingState,
    patch,
    goNext
  } = useOnboardingState();
  const {
    digitalSetup
  } = useOnboardingContent();
  const {
    digitalSetups: t0
  } = onboardingState;
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const digitalSetups = t1;
  let t2;
  if ($[2] !== digitalSetup.choices) {
    t2 = digitalSetup.choices.map(_temp);
    $[2] = digitalSetup.choices;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const choices = t2;
  let t3;
  if ($[4] !== digitalSetups || $[5] !== patch) {
    t3 = id_0 => patch({
      digitalSetups: digitalSetups.includes(id_0) ? digitalSetups.filter(selected => selected !== id_0) : [...digitalSetups, id_0]
    });
    $[4] = digitalSetups;
    $[5] = patch;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const toggleDigitalSetup = t3;
  const t4 = digitalSetups.length > 0;
  let t5;
  if ($[7] !== digitalSetup.heading) {
    t5 = <Heading level={1} variant="display" tone="inverse">{digitalSetup.heading}</Heading>;
    $[7] = digitalSetup.heading;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== digitalSetups || $[10] !== toggleDigitalSetup) {
    t6 = {
      multiple: true,
      selectedIds: digitalSetups,
      onToggle: toggleDigitalSetup
    };
    $[9] = digitalSetups;
    $[10] = toggleDigitalSetup;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== choices || $[13] !== t6) {
    t7 = <ChoiceList choices={choices} selection={t6} />;
    $[12] = choices;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== goNext || $[16] !== t4 || $[17] !== t5 || $[18] !== t7) {
    t8 = <OnboardingShell canContinue={t4} onContinue={goNext}>{t5}{t7}</OnboardingShell>;
    $[15] = goNext;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};

/* ----- icon registry ----- */

type IconProps = {
  name: IconKey;
};
const Icon = t0 => {
  const $ = _c(3);
  const {
    name
  } = t0;
  switch (name) {
    case "phone":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Smartphone className="size-6" />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "globe":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Globe className="size-6" />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "code":
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Code className="size-6" />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
};
function _temp(t0) {
  const {
    id,
    title,
    description,
    iconKey
  } = t0;
  return {
    id,
    title,
    description,
    icon: <Icon name={iconKey} />
  };
}